import { useState, useEffect } from 'react';
import { App } from '@capacitor/app';
import { supabase } from '../supabase';
import { isNative } from '../utils/platform';

// Android does not force updates, so the app has to say so itself. The
// server's app_config table holds the newest build number; the APK knows its
// own from getInfo(). Web always runs the newest deploy and never checks.
//
// 'required' locks the Home screen (build below android_min_build, i.e. one
// the backend no longer supports). 'available' is a dismissible card, and a
// dismissal sticks per target build: the card returns only when a build newer
// than the dismissed one appears.

export type UpdateState =
  | { status: 'none' }
  | { status: 'available'; latestBuild: number; storeUrl: string }
  | { status: 'required'; latestBuild: number; storeUrl: string };

const DISMISS_KEY = 'wot_update_dismissed_build';

export function useUpdateCheck(): {
  update: UpdateState;
  dismiss: () => void;
  openStore: () => void;
} {
  const [update, setUpdate] = useState<UpdateState>({ status: 'none' });

  useEffect(() => {
    if (!isNative) return;
    let alive = true;
    (async () => {
      try {
        const info = await App.getInfo();
        const myBuild = parseInt(info.build, 10);
        if (!Number.isFinite(myBuild)) return;

        const { data, error } = await supabase
          .from('app_config')
          .select('key, value')
          .in('key', ['android_latest_build', 'android_min_build', 'android_store_url']);
        if (error || !data || !alive) return;

        const cfg = Object.fromEntries(data.map((r) => [r.key, r.value]));
        const latest = parseInt(cfg.android_latest_build ?? '', 10);
        const min = parseInt(cfg.android_min_build ?? '', 10);
        const storeUrl = cfg.android_store_url
          ?? 'https://play.google.com/store/apps/details?id=app.worldoftrivia.wot';
        if (!Number.isFinite(latest)) return;

        if (Number.isFinite(min) && myBuild < min) {
          setUpdate({ status: 'required', latestBuild: latest, storeUrl });
          return;
        }
        if (myBuild < latest) {
          let dismissed = 0;
          try { dismissed = parseInt(localStorage.getItem(DISMISS_KEY) ?? '0', 10) || 0; } catch { /* ignore */ }
          if (dismissed < latest) {
            setUpdate({ status: 'available', latestBuild: latest, storeUrl });
          }
        }
      } catch { /* config unreachable: play on, never block on a check */ }
    })();
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    if (update.status !== 'available') return;
    try { localStorage.setItem(DISMISS_KEY, String(update.latestBuild)); } catch { /* ignore */ }
    setUpdate({ status: 'none' });
  };

  const openStore = () => {
    if (update.status === 'none') return;
    window.open(update.storeUrl, '_blank');
  };

  return { update, dismiss, openStore };
}
